import type { NotificationDelivery } from '@alison/ui';
import type { CadenceId, NotificationChannels, RoleId } from './notifications.types.js';

export interface NotifEvent {
  id: string;
  name: string;
  description: string;
}

export interface EventGroup {
  id: string;
  label: string;
  events: NotifEvent[];
}

export const CADENCES: { id: CadenceId; label: string }[] = [
  { id: 'realtime', label: 'Realtime' },
  { id: 'daily', label: 'Daily digest' },
  { id: 'weekly', label: 'Weekly' },
];

export function eventGroupsFor(providerName: string): EventGroup[] {
  return [
    {
      id: 'perf',
      label: 'Performance',
      events: [
        {
          id: 'kpi',
          name: 'KPI threshold breached',
          description: 'A tracked metric crosses a target you set.',
        },
        {
          id: 'insight',
          name: 'New Signal',
          description: `${providerName} surfaces a fresh creative or performance insight.`,
        },
      ],
    },
    {
      id: 'wf',
      label: 'Workflow',
      events: [
        {
          id: 'review',
          name: 'Creative needs review',
          description: 'A creative is waiting on your approval before it ships.',
        },
        {
          id: 'shipped',
          name: 'Action shipped to network',
          description: `${providerName} published or changed something on a connected account.`,
        },
      ],
    },
    {
      id: 'digest',
      label: 'Digest',
      events: [
        {
          id: 'weekly',
          name: 'Weekly summary',
          description: 'A rollup of performance, insights and shipped actions.',
        },
      ],
    },
  ];
}

export function allEventsFor(providerName: string): NotifEvent[] {
  return eventGroupsFor(providerName).flatMap((group) => group.events);
}

export const ROLES: { id: RoleId; label: string }[] = [
  { id: 'ua', label: 'UA Manager' },
  { id: 'creative', label: 'Creative Manager' },
  { id: 'cmo', label: 'CMO' },
  { id: 'cfo', label: 'CFO' },
];

/** Which channels can deliver; Settings and the bell share it so they never disagree. */
export function channelDelivery(channels: NotificationChannels | undefined): NotificationDelivery {
  return {
    slack:
      channels?.slack.status === 'connected' &&
      channels.slack.availability === 'available' &&
      channels.slack.channel !== '',
    email: channels?.email.status === 'connected',
  };
}
